import { z } from 'zod';

import { PageQuerySchema } from '../shared/pagination';
import { StrategyKeySchema } from './channel-binding';
import { ChannelSchema } from './channel-connection';
import { InboxSurfaceSchema } from './inbox-surface';

// agent_run_jobs itself stays an internal runtime ledger with no table mirror here — its
// payload is the verbatim case file and its dedupe key is the sweep's business. What this
// file declares is the admin read model over it, which IS a wire contract the admin app
// parses. The status tuple lives here so the Drizzle column and the admin filter share one
// definition and can never drift.
export const AGENT_RUN_STATUSES = ['queued', 'running', 'done', 'exhausted'] as const;
export const AgentRunStatusSchema = z.enum(AGENT_RUN_STATUSES);
export type AgentRunStatus = z.infer<typeof AgentRunStatusSchema>;

// The two states an operator acts on, named once so the admin tab and any future alert agree.
export const IN_FLIGHT_AGENT_RUN_STATUSES = ['queued', 'running'] as const;

export const AgentRunSummarySchema = z.object({
  id: z.uuid(),
  status: AgentRunStatusSchema,
  // Null until the run starts a session, and after a session is deleted.
  sessionId: z.uuid().nullable(),
  attempts: z.number().int(),
  // Counted apart from attempts: a run stopped by a deploy did not fail.
  resumes: z.number().int(),
  lastError: z.string().nullable(),
  runAfter: z.iso.datetime(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  // The binding that owns the run, resolved for display. The kill switch lives on the
  // binding, so the row links there rather than carrying its own toggle.
  channelBindingId: z.uuid(),
  bindingEnabled: z.boolean(),
  strategyKey: StrategyKeySchema,
  surface: InboxSurfaceSchema.nullable(),
  channel: ChannelSchema,
  connectionHandle: z.string(),
  agentName: z.string(),
  agentSlug: z.string(),
});
export type AgentRunSummary = z.infer<typeof AgentRunSummarySchema>;

export const ListAgentRunsQuerySchema = PageQuerySchema.extend({
  channelBindingId: z.uuid().optional(),
  status: AgentRunStatusSchema.optional(),
  // The "in-flight" tab: queued and running at once, which no single status value expresses.
  inFlight: z.stringbool().optional(),
});
export type ListAgentRunsQuery = z.infer<typeof ListAgentRunsQuerySchema>;

export const AgentRunPageSchema = z.object({
  items: z.array(AgentRunSummarySchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type AgentRunPage = z.infer<typeof AgentRunPageSchema>;

// The binding filter's options. Scoped to bindings that actually have runs: a dropdown of
// every binding would list the inline Instagram strategies, which never queue a run.
export const AgentRunBindingOptionSchema = z.object({
  id: z.uuid(),
  enabled: z.boolean(),
  strategyKey: StrategyKeySchema,
  channel: ChannelSchema,
  connectionHandle: z.string(),
  agentName: z.string(),
});
export type AgentRunBindingOption = z.infer<typeof AgentRunBindingOptionSchema>;
